import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BellRing, Mail, MessageCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChannelCard = ({ channel, icon: Icon, title, items, onToggle, onToggleAll, savingKey }) => {
  const allEnabled = items.length > 0 && items.every((item) => item.enabled);
  return (
    <Card className="border-gray-200" data-testid={`${channel}-notification-card`}>
      <CardHeader className="border-b border-gray-100 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2 text-lg"><Icon className="h-5 w-5 text-green-700" />{title}</CardTitle>
          <label className="flex items-center gap-2 text-sm font-medium" data-testid={`${channel}-all-alerts-control`}>
            <span>All alerts</span>
            <Switch checked={allEnabled} onCheckedChange={(enabled) => onToggleAll(channel, enabled)} disabled={savingKey === `${channel}:all`} data-testid={`${channel}-all-alerts-switch`} />
          </label>
        </div>
      </CardHeader>
      <CardContent className="divide-y divide-gray-100 px-5">
        {items.map((item) => (
          <div className="flex items-center justify-between gap-4 py-4" key={item.key} data-testid={`${channel}-alert-${item.key}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">{item.label}</p>
              {item.description && <p className="mt-1 text-xs text-gray-500">{item.description}</p>}
            </div>
            <Switch checked={item.enabled} onCheckedChange={(enabled) => onToggle(channel, item.key, enabled)} disabled={savingKey === `${channel}:${item.key}`} aria-label={`${item.label} ${channel} alerts`} data-testid={`${channel}-alert-${item.key}-switch`} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default function AdminNotifications() {
  const { authenticated, getAuthHeader } = useAdminAuth();
  const [settings, setSettings] = useState({ email: [], telegram: [] });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/notification-settings`, { headers: getAuthHeader() });
      setSettings({ email: response.data.email || [], telegram: response.data.telegram || [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to load notification settings');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { if (authenticated) load(); }, [authenticated, load]);

  const update = async (channel, key, enabled) => {
    const previous = settings;
    setSavingKey(`${channel}:${key}`);
    setSettings((current) => ({ ...current, [channel]: current[channel].map((item) => item.key === key ? { ...item, enabled } : item) }));
    try {
      await axios.put(`${API}/admin/notification-settings/${channel}/${key}`, { enabled }, { headers: getAuthHeader() });
      toast.success('Notification preference updated');
    } catch (error) {
      setSettings(previous);
      toast.error(error.response?.data?.detail || 'Unable to save notification preference');
    } finally { setSavingKey(''); }
  };

  const updateAll = async (channel, enabled) => {
    const previous = settings;
    setSavingKey(`${channel}:all`);
    setSettings((current) => ({ ...current, [channel]: current[channel].map((item) => ({ ...item, enabled })) }));
    try {
      await axios.put(`${API}/admin/notification-settings/${channel}/all`, { enabled }, { headers: getAuthHeader() });
      toast.success(`${channel === 'email' ? 'Email' : 'Telegram'} alerts updated`);
    } catch (error) {
      setSettings(previous);
      toast.error(error.response?.data?.detail || 'Unable to save notification preferences');
    } finally { setSavingKey(''); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="admin-notifications-page">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 pb-6">
        <div><h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900"><BellRing className="h-7 w-7 text-green-700" />Notification controls</h1><p className="mt-1 text-sm text-gray-500">Control future automated email and Telegram alerts. Credentials remain protected.</p></div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="notification-settings-refresh-button"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
      </div>
      {loading ? <div className="py-16 text-center text-sm text-gray-500" data-testid="notification-settings-loading">Loading notification controls…</div> : <div className="grid gap-6 xl:grid-cols-2"><ChannelCard channel="email" icon={Mail} title="Email automations" items={settings.email} onToggle={update} onToggleAll={updateAll} savingKey={savingKey} /><ChannelCard channel="telegram" icon={MessageCircle} title="Telegram alerts" items={settings.telegram} onToggle={update} onToggleAll={updateAll} savingKey={savingKey} /></div>}
    </div>
  );
}